import React, { useState, useRef, useEffect, MutableRefObject, ClipboardEvent, KeyboardEvent, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { FlosFormGroup } from './form-group/flos-form-group';
import { getId } from '../../utils';
import { callAll } from '../../utils/fp';

import type { FlosInputProps } from './input/flos-input';

type FieldProps<T> = T;

type FlosFieldRenderInputProps = {
    getInputProps: <T>(oriProps: FieldProps<T>) => FieldProps<T>;
};

type FlosFieldInternalProps = {
    renderInput: (renderInputProps: FlosFieldRenderInputProps) => JSX.Element;
    hasDropdown?: boolean;
    readOnlyHandler?: () => void | null;
};

type FlosFieldProps = {
    label?: string;
    wrapperClassName?: string;
    iconShape?: string;
    isValid?: boolean;
    isDirty?: boolean;
    errorText?: string;
    helpText?: string;
    required?: boolean;
    pattern?: string;
    maxLength?: number;
    minLength?: number;
    id?: string;
    disabled?: boolean;
    custom?: boolean;
    editable?: boolean;
    onValidityChange?: (isValid: boolean | undefined) => void;
    resetHandler?: () => void;
    showLength?: boolean;
    isOpen?: boolean;
    variant?: 'default' | 'typeahead';
};

type IconType = null | 'arrow' | 'close';

// Helper functions
const getChevronType = (
    variant: string,
    hasDropdown: boolean,
    disabled: boolean | undefined,
    resetHandler: (() => void) | undefined,
    isDirty: boolean | undefined,
    editable: boolean | undefined
): IconType => {
    if (variant === 'typeahead') {
        return hasDropdown && !disabled ? (resetHandler && isDirty ? 'close' : 'arrow') : null;
    }
    return !editable && hasDropdown && !disabled ? 'arrow' : null;
};

const FlosField = React.forwardRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, FlosFieldProps & FlosFieldInternalProps>(
    (
        {
            custom,
            disabled,
            editable,
            errorText,
            hasDropdown = false,
            helpText,
            iconShape,
            id,
            isValid: propIsValid,
            isDirty: externalIsDirty,
            label,
            maxLength = 0,
            minLength = 0,
            onValidityChange,
            renderInput,
            resetHandler,
            required,
            wrapperClassName,
            showLength = true,
            isOpen,
            variant = 'default',
            ...rest
        },
        ref: React.RefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>
    ) => {
        let inputRefInternal = useRef() as MutableRefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
        if (ref) inputRefInternal = ref as MutableRefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

        const initialValueLength = inputRefInternal.current?.value?.length || 0;

        // Separate state variables for better performance
        const [valueLength, setValueLength] = useState(initialValueLength);
        const [isFocused, setIsFocused] = useState(false);
        const [isDirty, setIsDirty] = useState(!!initialValueLength);
        const [isValid, setIsValid] = useState(propIsValid);
        const [isReadOnly, setIsReadOnly] = useState(!!editable);

        const inputId = id || getId();
        const formgroupRef = useRef<HTMLDivElement | null>(null);
        const hintText = isValid === false ? errorText : helpText;

        const hasHintText = !!hintText;
        const hasLengthCounter = maxLength >= 1;
        const showHelptext = hasHintText || hasLengthCounter;
        const shouldShowHelptextRow = showHelptext;
        const shouldShowLengthCounter = showLength && hasLengthCounter;

        // Memoized values
        const effectiveIsDirty = useMemo(
            () => (variant === 'typeahead' && typeof externalIsDirty !== 'undefined' ? externalIsDirty : isDirty),
            [variant, externalIsDirty, isDirty]
        );

        const determineIsValid = useCallback((currentIsDirty: boolean, valueLength: number, minLength: number, maxLength: number | undefined, propIsValid: boolean | undefined) => {
            if (typeof propIsValid !== 'undefined') return propIsValid;
            if (currentIsDirty && valueLength !== 0) {
                return valueLength >= minLength && (maxLength ? valueLength <= maxLength : true);
            }
            return undefined;
        }, []);

        // Event handlers
        const handleReadOnlyState = () => {
            setIsReadOnly((prev) => !prev);
        };

        const handleFocus = () => {
            setIsFocused(true);
        };

        const handleBlur = useCallback(() => {
            setIsFocused(false);
            const newIsValid = determineIsValid(isDirty, valueLength, minLength, maxLength, propIsValid);
            setIsValid(newIsValid);
            setIsReadOnly(!!editable);
        }, [isDirty, valueLength, minLength, maxLength, propIsValid, editable, determineIsValid]);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (variant === 'typeahead') {
                const val = event.target.value;
                setIsDirty(val !== '' && val !== undefined);
            } else {
                setIsDirty(true);
            }
        };

        const handleReset = () => {
            resetHandler?.();
            setIsDirty(false);
            setIsValid(undefined);
        };

        const handleResetKeyboard = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.code === 'Space' || e.code === 'Enter') {
                e.preventDefault();
                handleReset();
            }
        };

        const regExTest = useCallback((regex: RegExp, key: string) => regex.test(key), []);

        const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.currentTarget?.pattern) {
                const pattern = typeof event.currentTarget.pattern === 'string' ? new RegExp(event.currentTarget.pattern) : event.currentTarget.pattern;

                if (!regExTest(pattern, event.key)) event.preventDefault();
            }

            if (maxLength) {
                const ss = event.currentTarget.selectionStart || 0;
                const se = event.currentTarget.selectionEnd || 0;
                const selectionLength = se - ss;
                if (valueLength + 1 - selectionLength > maxLength) event.preventDefault();
            }
        };

        const handleKeyUp = () => {
            setIsDirty(true);
        };

        const handleKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Escape' && inputRefInternal.current) {
                inputRefInternal.current.blur();
            }
        };

        const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
            const pasted = event.clipboardData.getData('text');
            if (!maxLength) return;

            const ss = event.currentTarget.selectionStart || 0;
            const se = event.currentTarget.selectionEnd || 0;
            const selectionLength = se - ss;

            if (valueLength + pasted.length - selectionLength > maxLength) {
                event.preventDefault();
            }
        };

        // Helper functions
        const getHelpTextId = () => `${inputId}-error`;
        const getAriaDescribedBy = (oriDescribedBy?: string) => (oriDescribedBy ? getHelpTextId() : undefined);

        const getInputProps = <T extends FlosInputProps>(oriProps: T) => {
            const baseHandlers = {
                onBlur: callAll(oriProps.onBlur, handleBlur),
                onFocus: callAll(oriProps.onFocus, handleFocus),
                onChange: callAll(oriProps.onChange, handleChange),
                onKeyPress: callAll(oriProps.onKeyPress, handleKeyPress),
                onPaste: callAll(oriProps.onPaste, handlePaste),
            };

            const baseProps = {
                'aria-describedby': getAriaDescribedBy(oriProps['aria-describedby']),
                id: inputId,
                ref: inputRefInternal,
                ...baseHandlers,
            };

            const variantHandlers = {
                default: {
                    onKeyUp: callAll(oriProps.onKeyUp, handleKeyUp),
                    onKeyDown: callAll(oriProps.onKeyDown, handleKeydown),
                    readOnly: typeof editable !== 'undefined' ? isReadOnly : oriProps.readOnly,
                },
                typeahead: {
                    onKeyUp: oriProps.onKeyUp,
                    onKeyDown: oriProps.onKeyDown,
                },
            };

            return {
                ...oriProps,
                ...baseProps,
                ...variantHandlers[variant],
            };
        };

        // Effects
        useEffect(() => {
            const currentLength = inputRefInternal.current?.value?.length || 0;
            setValueLength(currentLength);
        }, [inputRefInternal.current?.value]);

        useEffect(() => {
            setIsValid(propIsValid);
        }, [propIsValid]);

        useEffect(() => {
            onValidityChange?.(isValid);
        }, [isValid, onValidityChange]);

        useEffect(() => {
            setIsDirty(!!externalIsDirty);
        }, [externalIsDirty, variant]);

        useEffect(() => {
            if (inputRefInternal.current && !isReadOnly && editable && variant === 'default') {
                inputRefInternal.current.focus();
            }
        }, [isReadOnly, editable, variant]);

        // Render helper functions
        const renderLabelIcons = () => {
            if (custom) return null;

            const chevron = getChevronType(variant, hasDropdown, disabled, resetHandler, effectiveIsDirty, editable);

            const flosIconClass = cx('flos-field-icon', {
                'flos-field-icon--with-label': !!label,
            });

            return (
                <>
                    {label && (
                        <label htmlFor={inputId} className="flos-label">
                            {label}
                            {required && '*'}
                        </label>
                    )}

                    {iconShape && <flos-icon class={cx(flosIconClass, 'is-indigo')} role="img" shape={iconShape} aria-label={iconShape} />}

                    {chevron === 'arrow' && (
                        <flos-icon
                            class={cx(flosIconClass, 'flos-field-icon--secondary is-logo-blue', {
                                'flos-field-icon--reversed': isFocused || isOpen,
                            })}
                            aria-label="arrow-down"
                            shape="arrow-down"
                            role="img"
                        />
                    )}

                    {chevron === 'close' && (
                        <flos-icon
                            onClick={handleReset}
                            onKeyDown={handleResetKeyboard}
                            class={cx(flosIconClass, 'flos-field-icon-reset flos-field-icon--secondary is-logo-blue')}
                            role="button"
                            aria-label="reset"
                            aria-description="Nulstil"
                            shape="close"
                            tabIndex={0}
                        />
                    )}

                    {editable && variant === 'default' && !chevron && (
                        <button className="flos-field-edit-button" tabIndex={0} onClick={handleReadOnlyState}>
                            <flos-icon class="is-interactive is-interactive--filled" shape="edit" aria-label="edit" />
                        </button>
                    )}
                </>
            );
        };

        const renderHelperText = () => {
            if (!shouldShowHelptextRow) return null;

            return (
                <div className="flos-field-helptext-row">
                    {hasHintText ? (
                        <div
                            className={cx('flos-field-helptext-col', {
                                'flos-field-helptext--valid': !disabled && isValid,
                                'flos-field-helptext--invalid': !disabled && isValid === false,
                            })}
                        >
                            {hintText}
                        </div>
                    ) : (
                        <div className="flos-field-helptext-col" />
                    )}

                    {shouldShowLengthCounter && (
                        <div className="flos-field-helptext-col flos-field-helptext-col--right">
                            {valueLength} / {maxLength}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <FlosFormGroup
                className={cx(
                    'flos-field',
                    {
                        'has-icon': iconShape,
                        'has-edit-button': editable && isReadOnly && variant === 'default',
                        'flos-field--with-helptext': shouldShowHelptextRow,
                        'has-chevron': hasDropdown,
                    },
                    wrapperClassName
                )}
                isValid={disabled ? undefined : isValid}
                required={required}
                ref={formgroupRef}
                {...rest}
            >
                {renderInput({ getInputProps: getInputProps as <T>(oriProps: FieldProps<T>) => FieldProps<T> })}
                {renderLabelIcons()}
                {renderHelperText()}
            </FlosFormGroup>
        );
    }
);

FlosField.displayName = 'FlosField';

const TypeaheadFlosField = React.forwardRef<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    Omit<FlosFieldProps & FlosFieldInternalProps, 'variant' | 'custom' | 'editable'>
>((props, ref) => {
    return <FlosField {...props} variant="typeahead" ref={ref} />;
});

TypeaheadFlosField.displayName = 'TypeaheadFlosField';

export type { FlosFieldProps, FlosFieldInternalProps, FlosFieldRenderInputProps };
export { FlosField, TypeaheadFlosField };
export default FlosField;
